import { useEffect } from 'react';

import errorLogger from '@picsart/growth-rc/services/errorLogger';
import { getContext } from 'configs/context';
import type { IGeneralContext } from '@picsart/recents-manager';
import { addSelfToSharedRecentsAuto } from '@picsart/recents-manager';
import useThemeChange from 'hooks/common/useThemeChange';

import { PACKAGE_JSON_VERSION, DATADOG_CLIENT_TOKEN, DATADOG_ENVIRONMENT, DATADOG_APP_ID, DATADOG_SERVICE } from 'utils/constants';
import sendPremiumContentInfo from 'utils/sendPremiumContentInfo';
import useOpen from 'hooks/useOpen';

import Main from '../components/sections/Main/index';
import Providers from '../providers/index';
import AppRoutes from '../components/common/AppRoutes/AppRoutes';

import packageJson from '../../package.json';
import miniappManifest from '../../extension-manifest.json';

import useStyles from './styles';

const dataDogInit = async () => {
  const { datadogRum } = await import('@datadog/browser-rum');

  if (DATADOG_ENVIRONMENT !== 'dev') {
    datadogRum.init({
      applicationId: DATADOG_APP_ID,
      clientToken: DATADOG_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: DATADOG_SERVICE,
      env: DATADOG_ENVIRONMENT,
      version: PACKAGE_JSON_VERSION,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 10,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      allowedTracingUrls: [
        { match: 'https://api.picsart.com', propagatorTypes: ['datadog'] },
        { match: 'https://ai.picsart.com', propagatorTypes: ['datadog'] },
        { match: 'https://aiweb.picsart.com', propagatorTypes: ['datadog'] },
        { match: 'https://upload.picsart.com', propagatorTypes: ['datadog'] },
        {
          match: 'https://upload-stage.picsartstage2.com',
          propagatorTypes: ['datadog'],
        },
        { match: 'https://picsartstage2.com', propagatorTypes: ['datadog'] },
      ],
      traceSampleRate: 100,
      traceContextInjection: 'sampled',
      actionNameAttribute: 'data-testid',
      trackViewsManually: true,
      trackSessionAcrossSubdomains: true,
      usePartitionedCrossSiteSessionCookie: true,
      useSecureSessionCookie: true,
    });
  }
};

dataDogInit();
const miniappOpenLog = 'opening the miniapp now';

const App = () => {
  useStyles();

  useEffect(() => {
    const contextMenuHandler = (e: MouseEvent) => {
      e.preventDefault();
    };

    window.addEventListener('contextmenu', contextMenuHandler);

    return () => {
      window.removeEventListener('contextmenu', contextMenuHandler);
    };
  }, []);

  const isOpen = useOpen(() => {
    errorLogger.leaveBreadcrumb(miniappOpenLog, { category: isOpen });
    sendPremiumContentInfo(true);
  });

  addSelfToSharedRecentsAuto(getContext() as unknown as IGeneralContext, packageJson, miniappManifest[0], { enableDebugLogs: false }).catch(error => {
    console.info('Failed to add self to recents:', error);
  });

  useThemeChange();

  return isOpen ? (
    <>
      <Providers>
        <Main>
          <AppRoutes />
        </Main>
      </Providers>
    </>
  ) : null;
};

export default App;
